import { AppBar, Theme, Toolbar, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { MenuStages, Routes } from '../../constants/enums';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagMenuVersion,
  selectFeatureFlagTaskRouter,
} from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { AgentStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectLoadedVersionDetails } from '../../selectors/menu';
import {
  selectActiveRestaurantDetails,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { RolePermissions } from '../../types/restaurant';
import {
  hasRoleAccess,
  selectedRestaurantAccessSelector,
} from '../../utils/restaurants';
import HeaderIndicators from '../HeaderIndicators';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ProfileMenu from './ProfileMenu';
import MenuVersionPicker from '../MenuVersionPicker';
import MenuVersionStage from '../MenuVersionStage';
import RestaurantsContainer from '../RestaurantsContainer';
import TaskRouterHeaderInfo from '../TaskRouterHeaderInfo';
import logger from '../../utils/logger';
import { copyTextToClipboard } from '../../utils';

type HeaderProps = {
  isTaskRouterPage?: boolean;
  handleAgentAvailability: (agentStatus: AgentStatuses) => void;
  sendAgentEvent?: Function;
  logout: Function;
  sendHealthScore?: Function;
};

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    flexGrow: 1,
    // Never shrinks: the version + sessionId stay on one line and fully visible;
    // the restaurant picker is the element that yields width when space is tight.
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  sessionId: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    // Always fully visible — never shrinks or truncates (the restaurant picker
    // yields width instead).
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  sessionIdText: {
    whiteSpace: 'nowrap',
  },
  restaurantPicker: {
    marginLeft: '4px',
  },
  menuVersionPicker: {
    marginLeft: '4px',
  },
  copyButton: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
    '&:hover': {
      backgroundColor: `rgba(244, 244, 244, 0.5) !important`,
    },
  },
}));

const Header: FC<HeaderProps> = ({
  handleAgentAvailability,
  sendAgentEvent,
  logout,
  sendHealthScore,
}) => {
  const location = useLocation();
  const taskRouterFeatureFlag = useAppSelector(selectFeatureFlagTaskRouter);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const currentTaskId = useAppSelector(selectCurrentTask);

  const isTaskRouterPage =
    taskRouterFeatureFlag && location.pathname.indexOf(Routes.taskRouter) >= 0;

  const classes = useStyles();
  const { restaurantCode, primaryRestaurantCode } = useSelector(
    selectActiveRestaurantDetails
  );
  const selectedStage = useSelector(selectedStageSelector);
  const sessionId = useAppSelector(selectSessionId) || '';
  const selectedRestaurantAccessLevel = useSelector(
    selectedRestaurantAccessSelector
  );
  const hasMenuVersionAccess = hasRoleAccess(
    RolePermissions.RestaurantManager,
    selectedRestaurantAccessLevel
  );
  const FEATURE_FLAG_MENU_VERSION = useSelector(selectFeatureFlagMenuVersion);
  const loadedVersionDetails = useSelector(selectLoadedVersionDetails);
  const { isBrandMenu, menuVersion } = loadedVersionDetails || {};

  const canChangeMenuVersion =
    FEATURE_FLAG_MENU_VERSION &&
    hasMenuVersionAccess &&
    restaurantCode &&
    !currentTaskId &&
    !primaryRestaurantCode;

  return (
    <AppBar position="static" data-header="true">
      {/* Lock to one 64px line so the header can't exceed the height App.tsx
          subtracts (calc(100% - 64px)) and push the page into vertical scroll. */}
      <Toolbar
        // disableGutters drops MUI's default .MuiToolbar-gutters padding, which
        // is a media-query rule (16px / 24px ≥600px) that otherwise overrides a
        // plain `px` in sx. With it off, our 8px padding actually applies.
        disableGutters
        sx={{
          minHeight: '64px !important',
          height: 64,
          overflowY: 'hidden',
          // 8px left edge padding (vs MUI's ~24px) and a single 4px gap between
          // every top-level item, so spacing is uniform. Right padding is 0 so
          // the last item (ProfileMenu) sits flush against the header's right
          // edge. Per-item margins/paddings are zeroed elsewhere so they don't
          // stack on top of this gap.
          pl: '8px',
          pr: 0,
          gap: '4px',
        }}
      >
        <img src="/presto_logo.svg" alt="Presto Logo" />
        <div className={classes.title}>
          {!isTaskRouterPage && restaurantCode && sessionId ? (
            <Typography
              className={classes.sessionId}
              component="div"
              // sx (not the makeStyles class) so MUI's Typography variant can't
              // override the size — keeps the sessionId identical to the other
              // header text (version / BM / time).
              sx={{ fontSize: '0.75rem' }}
            >
              <span className={classes.sessionIdText}>{sessionId}</span>
              <IconButton
                className={classes.copyButton}
                size="small"
                onClick={() => copyTextToClipboard(sessionId, logger)}
              >
                <ContentCopyIcon fontSize="small" style={{ color: 'white' }} />
              </IconButton>
            </Typography>
          ) : null}
        </div>
        {!isTaskRouterPage && (
          <>
            <HeaderIndicators />
            {canChangeMenuVersion && (
              <div className={classes.restaurantPicker}>
                <MenuVersionStage logout={logout} />
              </div>
            )}
            <div className={classes.restaurantPicker}>
              <RestaurantsContainer
                logout={logout}
                sendHealthScore={sendHealthScore}
              />
            </div>
            <div className={classes.restaurantPicker}>
              {canChangeMenuVersion &&
              selectedStage === MenuStages.PLAYGROUND ? (
                <MenuVersionPicker logout={logout} />
              ) : (
                <>
                  {menuVersion ? (
                    <span
                      style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >{`${isBrandMenu ? 'BM' : 'PRP'}: ${menuVersion}`}</span>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
        <ProfileMenu
          sendAgentEvent={sendAgentEvent}
          logout={logout}
          handleAgentAvailability={handleAgentAvailability}
          isTaskRouterPage={isTaskRouterPage}
        />
        {/* hitl_3_0 ON folds availability into ProfileMenu, so the standalone
            blue availability box is only rendered when the flag is OFF. */}
        {isTaskRouterPage && !isHitl3 && (
          <TaskRouterHeaderInfo
            handleAgentAvailability={handleAgentAvailability}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
